/**
 * Client-side stream state machine (§16.4, ADR-107, AC-032).
 *
 * The rule from `ui-projections/stream.ts` is that a gap must never be
 * visualised as fact. That module decides *whether* a resume is safe; this one
 * decides what the client's view becomes as a result, and it is separate from
 * the `EventSource` wiring so it can be tested without a browser.
 *
 * Two properties are enforced here and asserted by the tests:
 *
 *  1. A `snapshot` frame or a `reset_required` frame sets `discardScene`. The
 *     renderer treats that as "throw away every cached position", which is what
 *     stops a character being animated from a position that is fifty events old
 *     to the one it holds now — a journey that never happened.
 *  2. A frame whose cursor is not strictly ahead of the last *rendered* cursor
 *     is ignored. Duplicate and out-of-order deliveries are normal on a
 *     reconnect; applying them would move the scene backwards.
 */

import {
  decideResume,
  decodeCursor,
  type OfficeScene,
  type ReplayWindow,
  type ResumeDecision,
  type StreamFrame,
} from '@autonomous-team/ui-projections';

/** Connection status, distinct from data freshness — a live socket can carry stale facts. */
export type StreamStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'reset' | 'error';

/** One entry of the event timeline shown in S-10 and in the audit trail. */
export interface TimelineEntry {
  readonly cursor: string;
  readonly eventType: string;
  readonly summary: string;
  readonly receivedAtMs: number;
}

export interface StreamState {
  readonly status: StreamStatus;
  /** The scene as last delivered. `null` before the first snapshot and after a reset. */
  readonly scene: OfficeScene | null;
  /** Cursor of the last frame actually applied. Mirrors `CursorTracker`. */
  readonly cursor: string | null;
  /** Ledger sequence of that cursor, for the monotonicity guard. */
  readonly sequence: number;
  /**
   * Set for exactly one transition after a reset. The renderer must drop cached
   * positions while this is true; it is cleared by the next applied frame.
   */
  readonly discardScene: boolean;
  /** Human-readable explanation of the last transport decision, shown in the UI. */
  readonly notice: string | null;
  /** Bounded ring of recent events, newest first. */
  readonly timeline: readonly TimelineEntry[];
  readonly lastFrameAtMs: number | null;
  /** Replay window last advertised by the server; drives `decideResume` on reconnect. */
  readonly replayWindow: ReplayWindow | null;
}

/** Kept small: the timeline is an operator aid, not a log store. */
export const TIMELINE_LIMIT = 200;

/** The event type whose payload is a complete `OfficeScene` projection. */
export const SCENE_EVENT_TYPE = 'office.scene';

export const INITIAL_STREAM_STATE: StreamState = {
  status: 'idle',
  scene: null,
  cursor: null,
  sequence: -1,
  discardScene: false,
  notice: null,
  timeline: [],
  lastFrameAtMs: null,
  replayWindow: null,
};

/** Payload shape of a snapshot frame. The replay window travels with it (ADR-107). */
export interface SnapshotPayload {
  readonly scene: OfficeScene;
  readonly replayWindow?: ReplayWindow;
}

function sequenceOf(cursor: string): number {
  return decodeCursor(cursor)?.ledgerSequence ?? -1;
}

function push(timeline: readonly TimelineEntry[], entry: TimelineEntry): readonly TimelineEntry[] {
  return [entry, ...timeline].slice(0, TIMELINE_LIMIT);
}

/**
 * Applies one frame.
 *
 * `decode` converts an untyped payload into a scene; it is injected so this
 * module stays free of JSON validation concerns and so tests can supply a
 * decoder that rejects, exercising the "frame failed to apply" path. A frame
 * that fails to decode does **not** advance the cursor: `CursorTracker` tracks
 * the last *rendered* position precisely so an unapplied frame is not later
 * treated as seen.
 */
export function applyFrame(
  state: StreamState,
  frame: StreamFrame,
  nowMs: number,
  decode: (payload: unknown) => OfficeScene | null,
): StreamState {
  switch (frame.kind) {
    case 'reset_required':
      // The server knows our view is unusable. Everything cached is now a guess.
      return {
        ...state,
        status: 'reset',
        scene: null,
        cursor: null,
        sequence: -1,
        discardScene: true,
        // Not "the server ended the stream" — it did not. The stream stays open
        // and the replacement snapshot is the next frame on it. Saying otherwise
        // presented a routine cold start as a failure, in red, on screen.
        notice: `The scene was discarded rather than continued: ${frame.reason}`,
        lastFrameAtMs: nowMs,
      };

    case 'snapshot': {
      const payload = frame.payload as SnapshotPayload | null;
      const scene = payload === null ? null : decode(payload.scene);
      if (scene === null) {
        return {
          ...state,
          status: 'error',
          notice: 'A snapshot frame could not be decoded. The previous scene was discarded rather than kept.',
          scene: null,
          cursor: null,
          sequence: -1,
          discardScene: true,
          lastFrameAtMs: nowMs,
        };
      }
      // A snapshot is an atomic replacement: the client resets rather than
      // continuing to animate from a frame it can no longer justify.
      return {
        status: 'live',
        scene,
        cursor: frame.cursor,
        sequence: sequenceOf(frame.cursor),
        discardScene: true,
        notice: state.cursor === null ? null : 'The stream resumed with a snapshot; cached positions were discarded.',
        timeline: push(state.timeline, {
          cursor: frame.cursor,
          eventType: 'snapshot',
          summary: 'Atomic snapshot received; scene state replaced.',
          receivedAtMs: nowMs,
        }),
        lastFrameAtMs: nowMs,
        replayWindow: payload?.replayWindow ?? state.replayWindow,
      };
    }

    case 'heartbeat':
      // A comment frame. It proves the transport is alive and says nothing about
      // the facts, so it must not touch the scene or the cursor.
      return { ...state, status: state.scene === null ? state.status : 'live', lastFrameAtMs: nowMs };

    case 'event': {
      const sequence = sequenceOf(frame.cursor);
      if (sequence <= state.sequence) {
        // Replay after a reconnect legitimately re-delivers frames we rendered.
        return { ...state, lastFrameAtMs: nowMs };
      }

      if (frame.eventType !== SCENE_EVENT_TYPE) {
        return {
          ...state,
          status: 'live',
          cursor: frame.cursor,
          sequence,
          discardScene: false,
          timeline: push(state.timeline, {
            cursor: frame.cursor,
            eventType: frame.eventType,
            summary: summarise(frame.eventType, frame.payload),
            receivedAtMs: nowMs,
          }),
          lastFrameAtMs: nowMs,
        };
      }

      const scene = decode(frame.payload);
      if (scene === null) {
        // Not applied, so not marked rendered. The cursor stays where it was and
        // the next reconnect will ask for this frame again.
        return {
          ...state,
          status: 'error',
          notice: 'A scene frame could not be decoded and was not applied. The cursor was not advanced.',
          lastFrameAtMs: nowMs,
        };
      }

      return {
        ...state,
        status: 'live',
        scene,
        cursor: frame.cursor,
        sequence,
        discardScene: false,
        notice: null,
        timeline: push(state.timeline, {
          cursor: frame.cursor,
          eventType: frame.eventType,
          summary: `Scene projection updated (${scene.mode}).`,
          receivedAtMs: nowMs,
        }),
        lastFrameAtMs: nowMs,
      };
    }

    default: {
      const exhaustive: never = frame;
      throw new Error(`unhandled frame ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** Marks a connection attempt, preserving whatever we already legitimately hold. */
export function markConnecting(state: StreamState): StreamState {
  return { ...state, status: state.cursor === null ? 'connecting' : 'reconnecting', discardScene: false };
}

export function markError(state: StreamState, message: string): StreamState {
  return { ...state, status: 'error', notice: message };
}

/** Clears the one-shot reset flag once the renderer has honoured it. */
export function acknowledgeDiscard(state: StreamState): StreamState {
  return state.discardScene ? { ...state, discardScene: false } : state;
}

/**
 * Decides how to reconnect, and whether the current scene may survive it.
 *
 * `decideResume` from `ui-projections` owns the decision; this wrapper only
 * turns it into the client's next state. When the window is not yet known the
 * client is conservative and asks for a cold start, because "probably still in
 * the window" is exactly the guess ADR-107 forbids.
 */
export function planResume(state: StreamState): { readonly decision: ResumeDecision; readonly next: StreamState } {
  const window = state.replayWindow ?? { oldestRetainedSequence: 0, latestSequence: -1, maxReplayEvents: 0 };
  const decision = decideResume(state.cursor, window);
  if (!decision.resetRequired) {
    return { decision, next: { ...state, status: 'reconnecting', notice: decision.reason } };
  }
  return {
    decision,
    next: {
      ...state,
      status: 'reconnecting',
      scene: null,
      cursor: null,
      sequence: -1,
      discardScene: true,
      notice: `Resuming with a snapshot: ${decision.reason}. Nothing was animated across the gap.`,
    },
  };
}

function summarise(eventType: string, payload: unknown): string {
  if (payload !== null && typeof payload === 'object' && 'summary' in payload) {
    const summary = (payload as { readonly summary?: unknown }).summary;
    if (typeof summary === 'string') return summary;
  }
  return eventType;
}
